import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useImpersonationPath } from '@/hooks/useImpersonationPath'
import { NAV_ITEMS } from '@/config/navigation'
import { AppLogo } from '@/components/ui/AppLogo'
import { useBrandingStore } from '@/stores/branding.store'
import {
  House,
  Users,
  UsersThree,
  FileText,
  Gear,
  X,
  Storefront,
  Buildings,
  LinkSimple,
  Receipt,
} from 'phosphor-react'

const NAV_ICONS: Record<string, React.ReactNode> = {
  '/': <House className="w-5 h-5" weight="regular" />,
  '/users': <Users className="w-5 h-5" weight="regular" />,
  '/resellers': <Storefront className="w-5 h-5" weight="regular" />,
  '/wholesalers': <Buildings className="w-5 h-5" weight="regular" />,
  '/attached-resellers': <LinkSimple className="w-5 h-5" weight="regular" />,
  '/attached-clients': <LinkSimple className="w-5 h-5" weight="regular" />,
  '/clients': <UsersThree className="w-5 h-5" weight="regular" />,
  '/facturation': <Receipt className="w-5 h-5" weight="regular" />,
  '/logs': <FileText className="w-5 h-5" weight="regular" />,
  '/settings': <Gear className="w-5 h-5" weight="regular" />,
}

interface SidebarProps {
  isOpen: boolean
  isCollapsed: boolean
  onClose: () => void
  onToggleCollapse: () => void
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, isCollapsed, onClose }) => {
  const location = useLocation()
  const { isImpersonating, hasAnyRole, user } = useAuth()
  const toPath = useImpersonationPath()
  const appName = useBrandingStore(state => state.branding.appName)

  const filteredNavItems = NAV_ITEMS.filter(item => {
    if (item.accessCheck && !item.accessCheck(user)) return false
    return !item.roles?.length || hasAnyRole(item.roles)
  })

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/80 z-[45] lg:hidden" onClick={onClose} />}

      <aside
        className={`fixed ${isImpersonating ? 'top-10 bottom-0' : 'inset-y-0'} left-0 z-[50] bg-white shadow-sm transform transition-all duration-300 ease-in-out w-64 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}`}
      >
        <div className="flex flex-col h-full">
          <div
            className={`flex items-center gap-3 py-4  px-6 ${
              isCollapsed ? 'lg:justify-center lg:px-4' : ''
            }`}
          >
            <AppLogo size="sm" />
            {(!isCollapsed || isOpen) && (
              <span className="text-xl font-bold text-[var(--color-text-primary)] truncate min-w-0">
                {appName}
              </span>
            )}
            <button
              onClick={onClose}
              className="ml-auto lg:hidden flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[var(--color-gray-100)] transition-colors"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5 text-[var(--color-text-primary)]" weight="regular" />
            </button>
          </div>

          <nav
            className={`flex-1 py-6 space-y-1 overflow-y-auto transition-all duration-300 px-4 ${
              isCollapsed ? 'lg:px-2' : ''
            }`}
          >
            {filteredNavItems.map(item => {
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={toPath(item.path)}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      onClose()
                    }
                  }}
                  className={`flex items-center gap-3 rounded-lg transition-colors group px-4 py-3 ${
                    isCollapsed ? 'lg:justify-center lg:px-2' : ''
                  } ${
                    isActive
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-gray-100)] hover:text-[var(--color-text-primary)]'
                  }`}
                  title={isCollapsed && !isOpen ? item.label : undefined}
                >
                  <div className="flex-shrink-0">{NAV_ICONS[item.path]}</div>
                  {(!isCollapsed || isOpen) && (
                    <span className="font-medium whitespace-nowrap">{item.label}</span>
                  )}
                </Link>
              )
            })}
          </nav>
        </div>
      </aside>
    </>
  )
}
